import axios, { AxiosRequestConfig } from 'axios';
import { hmac } from '@kakaopay-common/payweb-bridge';

import uuid from '~/configs/api/axios/helpers/uuid';
import {
  avoidCancelError,
  convertToCamelCaseOnError,
  convertToCamelCaseOnResponse,
  convertToSnakeCaseOnRequest,
  suspendRequestOnPause,
} from '~/configs/api/axios/interceptor';
import getAccessToken from '~/jsapi/interface/getAccessToken';
import { isPayWebview } from '~/utils/appInfo';

/**
 * /signature path에 대해서 jsapi의 hmac기능을 통해 헤더에 관련값 설정
 * @param config: AxiosRequestConfig
 * @returns AxiosRequestConfig;
 */
const setHmacHeaders = async (config: AxiosRequestConfig) => {
  try {
    if (config.url?.includes('/signature')) {
      const data = await hmac(JSON.stringify(config.data));
      const { signature, seed } = data.result;

      config.headers = {
        ...config.headers,
        'X-PaySec-Hmac-Seed': seed,
        'X-PaySec-Hmac-Signature': signature,
      };
    }
  } catch (error) {
    console.error(`hmac error`);
  }

  return config;
};

/**
 * 서버로 요청을 보낼때 헤더에 인증토큰 및 hmac 정보를 넣어서 보낼 수 있게끔 처리하는 인터셉터
 * @param config: AxiosRequestConfig
 * @returns AxiosRequestConfig;
 */
const appendSecAuthToken = async (config: AxiosRequestConfig) => {
  let accessToken = '';

  try {
    if (isPayWebview) {
      const { result } = await getAccessToken();
      accessToken = result.accessToken;
      // 페이웹뷰가 아닌 개발환경에선 'ModHeader' 로 직접 토큰을 넣어준다.
    }
  } catch (e) {
    console.error(e);
  }

  return {
    ...config,
    headers: {
      ...config.headers,
      'X-PaySec-Authorization': `SecAuth ${accessToken}-${
        (await uuid.getUuid()) || ''
      }`,
    },
  };
};

const secAxios = axios.create();

secAxios.defaults.timeout = 5000;
secAxios.defaults.withCredentials = true;

// API 요청시 Header에 X-PaySec-Authorization 추가
secAxios.interceptors.request.use(appendSecAuthToken);

// 전자서명 API 요청시 Header에 전자서명 원문값 추가
secAxios.interceptors.request.use(setHmacHeaders);

// API 요청시 camelCase to snake_case 처리
secAxios.interceptors.request.use(convertToSnakeCaseOnRequest);

// API 요청시 Pause인 경우 Resume시까지 대기
secAxios.interceptors.request.use(suspendRequestOnPause);

// API 응답시 snake_case to CamelCase
secAxios.interceptors.response.use(
  convertToCamelCaseOnResponse,
  convertToCamelCaseOnError
);

// 페이 증권 계좌 사고 공통 에러 처리
// secAxios.interceptors.response.use(undefined, showErrorMessageWhenPaySecCommonAccountError);

// API Cancel 에러는 무시하도록 처리
secAxios.interceptors.response.use(undefined, avoidCancelError);

// API 에러 발생시 PayApiError Instance로 Throw
// secAxios.interceptors.response.use(undefined, throwErrorInstance);

// Mutate 에러들은 인터셉터까지 태우지 않고 여기서 해결
// secAxios.interceptors.response.use(undefined, handleMutateError);

export default secAxios;
